import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Navbar from './Navbar';
import Sidebar from './Sidebar';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import format from 'date-fns/format';
import startOfMonth from 'date-fns/startOfMonth';
import endOfMonth from 'date-fns/endOfMonth';
import eachDayOfInterval from 'date-fns/eachDayOfInterval';
import isSameMonth from 'date-fns/isSameMonth';
import isSameDay from 'date-fns/isSameDay';
import addMonths from 'date-fns/addMonths';
import subMonths from 'date-fns/subMonths';
import parseISO from 'date-fns/parseISO';
import Loader from '../components/Loader';
import { FaCalendarAlt, FaUser, FaPlus, FaChevronLeft, FaChevronRight, FaClock, FaStethoscope, FaNotesMedical, FaCalendar, FaTimes } from 'react-icons/fa';

const Calendar = () => {
  const { user, token } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showModal, setShowModal] = useState(false);
  const [appointments, setAppointments] = useState([]);

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [newAppointment, setNewAppointment] = useState({
    date: '',
    time: '',
    type: 'checkup',
    notes: '',
    status: 'pending',
    patient: '',
    doctor: user?.id || '',
    doctorModel: 'Dentist',
    chiefComplaint: '',
  });

  const [selectedPatient, setSelectedPatient] = useState(null);

  const [showPatientModal, setShowPatientModal] = useState(false);
  const [patientSearchModal, setPatientSearchModal] = useState('');
  const [allPatients, setAllPatients] = useState([]);
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [loadingPatients, setLoadingPatients] = useState(false);

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });



  // Fetch all dentist appointments
  useEffect(() => {
    const fetchAppointments = async () => {
      console.log('Fetching appointments for user:', user);
      console.log('User ID:', user?.id);
      console.log('User role:', user?.role);
      console.log('Token exists:', !!token);
      
      if (!user?.id || !token) {
        setError('Please log in as a dentist to access the calendar.');
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const config = { headers: { Authorization: `Bearer ${token}` } };
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/appointments/my-appointments`, config);
        setAppointments(response.data.map(appt => {
          // Handle different date formats from Firestore
          let date;
          if (typeof appt.date === 'string') {
            date = parseISO(appt.date);
          } else if (appt.date && appt.date.toDate) {
            // Firestore timestamp
            date = appt.date.toDate();
          } else if (appt.date instanceof Date) {
            date = appt.date;
          } else if (appt.date && appt.date._seconds) {
            // Firestore timestamp object
            date = new Date(appt.date._seconds * 1000);
          } else {
            // Fallback to current date if date is invalid
            console.warn('Invalid date format for appointment:', appt.date);
            date = new Date();
          }
          
          // Check if the parsed date is valid
          if (isNaN(date.getTime())) {
            console.warn('Invalid date after parsing:', appt.date);
            date = new Date();
          }
          
          return {
            ...appt,
            date: date,
          };
        }));
      } catch (err) {
        console.error('Error fetching appointments:', err.response?.data || err.message);
        setError(err.response?.data?.message || 'Failed to fetch appointments');
      } finally {
        setLoading(false);
      }
    };
    fetchAppointments();
  }, [user?.id, token]);

  // Removed available slots fetching since we're using manual time input

  const validateForm = () => {
    if (!newAppointment.date) return 'Date is required';
    if (!newAppointment.time) return 'Time is required';
    if (!newAppointment.type) return 'Appointment type is required';
    if (!newAppointment.patient) return 'Patient is required';
    return '';
  };

  const handleAddAppointment = () => {
    console.log('Opening appointment modal for user:', user);
    console.log('Full user object:', JSON.stringify(user, null, 2));
    console.log('User ID:', user?.id);
    console.log('User role:', user?.role);
    console.log('User email:', user?.email);
    console.log('User name:', user?.name);
    
    // Check if user is actually a dentist
    if (user?.role !== 'dentist') {
      setError(`Access denied. Your role is '${user?.role}', but this page requires 'dentist' role.`);
      return;
    }
    
    setShowModal(true);
    setError('');
    setNewAppointment({
      date: format(selectedDate, 'yyyy-MM-dd'),
      time: '',
      type: 'checkup',
      notes: '',
      status: 'pending',
      patient: '',
      doctor: user?.id || '',
      doctorModel: 'Dentist',
      chiefComplaint: '',
    });
  };

  const handleSubmitAppointment = async (e) => {
    e.preventDefault();
    if (!user?.id) {
      setError('You must be logged in as a dentist to access the calendar.');
      return;
    }
    
    // Check if user is actually a dentist
    if (user?.role !== 'dentist') {
      setError(`Access denied. Your role is '${user?.role}', but this page requires 'dentist' role.`);
      return;
    }
    
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    // Check for time conflict
    const hasConflict = await checkTimeConflict(newAppointment.date, newAppointment.time);
    if (hasConflict) {
      setError('There is a time conflict. Please choose a different time.');
      return;
    }

    const apiUrl = process.env.REACT_APP_API_URL;
    console.log('API URL from environment:', apiUrl);
    
    if (!apiUrl) {
      setError('API URL not configured. Please check your environment variables.');
      return;
    }

    try {
      // For dentists, we need to use the user.id as the dentistId
      // The backend looks for dentists using the document ID in the DENTISTS collection
      let dentistId = user.id; // Use the user's ID directly since dentists are stored with their ID as the document ID
      
      console.log('Selected dentistId for appointment:', dentistId);
      console.log('User object:', user);
      console.log('User ID being sent:', dentistId);
      console.log('User role:', user.role);
      
      // Ensure we have the correct user ID and role
      const appointmentData = {
        ...newAppointment,
        doctor: dentistId, // This should match the document ID in the DENTISTS collection
        doctorModel: 'Dentist',
        // Make sure chiefComplaint is not undefined if empty
        chiefComplaint: newAppointment.chiefComplaint || ''
      };
      
      console.log('Creating appointment with data:', appointmentData);
      console.log('Full appointments endpoint:', `${apiUrl}/api/appointments`);
      
      const response = await axios.post(`${apiUrl}/api/appointments`, appointmentData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log('Appointment created:', response.data);
      
      // Handle date parsing for the new appointment
      let date;
      if (typeof response.data.date === 'string') {
        date = parseISO(response.data.date);
      } else if (response.data.date && response.data.date.toDate) {
        // Firestore timestamp
        date = response.data.date.toDate();
      } else if (response.data.date instanceof Date) {
        date = response.data.date;
      } else if (response.data.date && response.data.date._seconds) {
        // Firestore timestamp object
        date = new Date(response.data.date._seconds * 1000);
      } else {
        // Fallback to current date if date is invalid
        console.warn('Invalid date format for new appointment:', response.data.date);
        date = new Date();
      }
      
      // Check if the parsed date is valid
      if (isNaN(date.getTime())) {
        console.warn('Invalid date after parsing:', response.data.date);
        date = new Date();
      }
      
      setAppointments([...appointments, { ...response.data, date: date }]);
      setNewAppointment({
        date: '',
        time: '',
        type: 'checkup',
        notes: '',
        status: 'pending',
        patient: '',
        doctor: user.id,
        doctorModel: 'Dentist',
        chiefComplaint: '',
      });
      setShowModal(false);
      setError('');
    } catch (err) {
      console.error('Error creating appointment:', err.response?.data || err.message);
      console.error('Error response:', err.response?.data);
      console.error('Error status:', err.response?.status);
      
      // Handle specific error cases
      if (err.response?.data?.message?.includes('Dentist not found')) {
        setError('Dentist profile not found. Please contact your administrator to verify your account. The system is looking for a dentist with ID: ' + user.id);
      } else if (err.response?.status === 401) {
        setError('Authentication error. Please log in again.');
      } else if (err.response?.status === 403) {
        setError('Access denied. You do not have permission to create appointments.');
      } else if (err.response?.status === 404) {
        setError('Appointments service not found (404). This usually means: 1) Backend server is not running, 2) API endpoint is incorrect, or 3) Network issue. Please contact support.');
      } else {
        setError(err.response?.data?.message || 'Failed to add appointment. Please try again.');
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    console.log('Input changed:', { name, value });
    setNewAppointment({ ...newAppointment, [name]: value });
  };



  const openPatientModal = async () => {
    console.log('Opening patient modal for user:', user);
    console.log('User role:', user?.role);
    console.log('User ID:', user?.id);
    console.log('Token exists:', !!token);
    
    setShowPatientModal(true);
    setPatientSearchModal('');
    await fetchAllPatients();
  };

  const fetchAllPatients = async () => {
    try {
      setLoadingPatients(true);
      console.log('Fetching patients for dentist:', user?.id);
      console.log('Full user object:', user);
      
      // Use the correct endpoint for dentists - /api/patients (getPatientsByDoctor)
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/patients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Patients API response:', response.data);
      
      const patients = Array.isArray(response.data) ? response.data : 
                      (response.data?.data ? response.data.data : []);
      
      console.log('Processed patients:', patients);
      
      if (patients.length === 0) {
        console.log('No patients found. This might mean:');
        console.log('1. The dentist has no assigned patients');
        console.log('2. The drId field in patients doesn\'t match the user ID');
        console.log('3. There\'s a data structure issue');
        console.log('4. The user ID being used:', user?.id);
      }
      
      setAllPatients(patients);
      setFilteredPatients(patients);
    } catch (err) {
      console.error('Error fetching patients:', err);
      console.error('Error response:', err.response?.data);
      console.error('Error status:', err.response?.status);
      
      if (err.response?.status === 403) {
        setError('Access denied. You do not have permission to view patients.');
        
        // Try alternative approach - search for patients with empty query to get all
        console.log('Trying alternative approach with search endpoint...');
        try {
          const searchResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/patients/search`, {
            params: { q: '' },
            headers: { Authorization: `Bearer ${token}` }
          });
          
          console.log('Search endpoint response:', searchResponse.data);
          const searchPatients = Array.isArray(searchResponse.data) ? searchResponse.data : [];
          
          if (searchPatients.length > 0) {
            setAllPatients(searchPatients);
            setFilteredPatients(searchPatients);
            setError(''); // Clear the error
            console.log('Successfully fetched patients via search endpoint');
          }
        } catch (searchErr) {
          console.error('Search endpoint also failed:', searchErr);
        }
      } else if (err.response?.status === 401) {
        setError('Authentication error. Please log in again.');
      } else {
        setError('Failed to fetch patients. Please try again.');
      }
    } finally {
      setLoadingPatients(false);
    }
  };

  const handlePatientModalSearch = (e) => {
    const searchTerm = e.target.value.toLowerCase();
    setPatientSearchModal(searchTerm);
    
    if (!searchTerm.trim()) {
      setFilteredPatients(allPatients);
    } else {
      const filtered = allPatients.filter(patient => 
        (patient.fullName && patient.fullName.toLowerCase().includes(searchTerm)) ||
        (patient.nationalId && patient.nationalId.toLowerCase().includes(searchTerm)) ||
        (patient.name && patient.name.toLowerCase().includes(searchTerm))
      );
      setFilteredPatients(filtered);
    }
  };

  const selectPatientFromModal = (patient) => {
    setSelectedPatient(patient);
    setNewAppointment(prev => ({ 
      ...prev, 
      patient: patient.nationalId || patient.id || patient._id 
    }));
    setShowPatientModal(false);
    setPatientSearchModal('');
  };

  const checkTimeConflict = async (date, time) => {
    if (!date || !time) return false;
    
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/appointments/check-conflict`, {
        params: {
          doctorId: user.id, // Use user.id directly for dentists
          doctorModel: 'Dentist',
          date: date,
          time: time
        },
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data.hasConflict;
    } catch (err) {
      console.error('Error checking time conflict:', err);
      return false;
    }
  };

  const handlePrevMonth = () => setCurrentDate(subMonths(currentDate, 1));
  const handleNextMonth = () => setCurrentDate(addMonths(currentDate, 1));
  const handleDateClick = (day) => {
    setSelectedDate(day);
    setNewAppointment({ ...newAppointment, date: format(day, 'yyyy-MM-dd') });
  };
  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  const getAppointmentsForDay = (day) => appointments.filter((appt) => isSameDay(appt.date, day));

  // Animation variants
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 },
  };

  if (loading) {
    return <Loader />;
  }

  if (!user || !user.id) {
    console.log('User object not properly loaded:', user);
    return (
      <div className="flex h-screen bg-gray-50">
        <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Navbar toggleSidebar={toggleSidebar} />
          <main className="flex-1 overflow-y-auto p-4 md:p-6 bg-gradient-to-br from-blue-50 to-white">
            <div className="max-w-7xl mx-auto">
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="text-center max-w-md bg-white p-8 rounded-xl shadow-sm border border-gray-100"
              >
                <div className="text-red-500 mb-4">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-12 w-12 mx-auto"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77-1.333.192 3 1.732 3z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Access Denied</h3>
                <p className="text-gray-500 mb-6">
                  {!user ? 'User not loaded. Please wait...' : 'Please log in as a dentist to view this page.'}
                </p>
                {user && (
                  <div className="text-xs text-gray-400 mt-2">
                    <p>User object: {JSON.stringify(user, null, 2)}</p>
                  </div>
                )}
              </motion.div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  // Check if user is actually a dentist
  if (user.role !== 'dentist') {
    console.log('User is not a dentist:', user);
    return (
      <div className="flex h-screen bg-gray-50">
        <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Navbar toggleSidebar={toggleSidebar} />
          <main className="flex-1 overflow-y-auto p-4 md:p-6 bg-gradient-to-br from-blue-50 to-white">
            <div className="max-w-7xl mx-auto">
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="text-center max-w-md bg-white p-8 rounded-xl shadow-sm border border-gray-100"
              >
                <div className="text-red-500 mb-4">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-12 w-12 mx-auto"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77-1.333.192 3 1.732 3z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Access Denied</h3>
                <p className="text-gray-500 mb-6">
                  Your role '{user.role}' does not have access to this page. This page is only for dentists.
                </p>
                <div className="text-xs text-gray-400 mt-2">
                  <p>User object: {JSON.stringify(user, null, 2)}</p>
                </div>
              </motion.div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Navbar toggleSidebar={toggleSidebar} />
        <main className="flex-1 overflow-y-auto p-6 bg-gradient-to-br from-[rgba(0,119,182,0.05)] to-white">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
                <div>
                  <h1 className="text-3xl md:text-4xl font-bold text-[#0077B6] mb-1">Appointment Calendar</h1>
                  <p className="text-[#333333]">Manage your schedule, {user?.name || 'Dentist'}</p>
                </div>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleAddAppointment}
                  className="w-full sm:w-auto bg-gradient-to-r from-[#0077B6] to-[#20B2AA] text-white px-6 py-3 rounded-full font-medium transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center"
                >
                  <FaPlus className="h-5 w-5 mr-2" />
                  Add Appointment
                </motion.button>
              </div>

              {error && !showModal && (
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg shadow-sm"
                >
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-red-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <p className="text-red-700 font-medium">{error}</p>
                  </div>
                </motion.div>
              )}

              <motion.div
                variants={container}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                className="grid grid-cols-1 lg:grid-cols-2 gap-6"
              >
                {/* Calendar Section */}
                <motion.div
                  variants={item}
                  className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 border border-[rgba(0,119,182,0.1)] hover:border-[#20B2AA] flex flex-col"
                >
                  <div className="flex items-center justify-between mb-6">
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={handlePrevMonth}
                      className="p-2 text-[#0077B6] hover:bg-[rgba(0,119,182,0.05)] rounded-full"
                    >
                      <FaChevronLeft className="h-5 w-5" />
                    </motion.button>
                    <h2 className="text-xl font-bold text-[#0077B6]">{format(currentDate, 'MMMM yyyy')}</h2>
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={handleNextMonth}
                      className="p-2 text-[#0077B6] hover:bg-[rgba(0,119,182,0.05)] rounded-full"
                    >
                      <FaChevronRight className="h-5 w-5" />
                    </motion.button>
                  </div>
                  <div className="grid grid-cols-7 gap-2 mb-4">
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                      <div key={day} className="text-center text-sm font-semibold text-gray-500">
                        {day}
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-7 gap-2">
                    {daysInMonth.map((day, i) => {
                      const dayAppointments = getAppointmentsForDay(day);
                      const isSelected = isSameDay(day, selectedDate);
                      const isCurrentMonth = isSameMonth(day, currentDate);
                      const isToday = isSameDay(day, new Date());

                      return (
                        <motion.div
                          key={i}
                          onClick={() => handleDateClick(day)}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className={`relative p-2 rounded-lg cursor-pointer transition-all duration-200 flex items-center justify-center ${
                            isSelected ? 'bg-[rgba(0,119,182,0.1)] border-2 border-[#0077B6] shadow-inner' :
                            isToday ? 'bg-[rgba(0,119,182,0.05)] border border-[#20B2AA]' :
                            'hover:bg-[rgba(0,119,182,0.05)] border border-gray-200'
                          } ${!isCurrentMonth ? 'opacity-60' : ''}`}
                        >
                          <span className={`text-sm font-medium ${
                            isSelected ? 'text-[#0077B6]' : isToday ? 'text-[#20B2AA]' : 'text-[#333333]'
                          }`}>
                            {format(day, 'd')}
                          </span>
                          {dayAppointments.length > 0 && (
                            <span className="absolute -top-1 -right-1 h-4 w-4 bg-[#0077B6] text-white text-xs rounded-full flex items-center justify-center">
                              {dayAppointments.length}
                            </span>
                          )}
                        </motion.div>
                      );
                    })}
                  </div>
                </motion.div>

                {/* Appointments for Selected Date */}
                <motion.div
                  variants={item}
                  className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 border border-[rgba(0,119,182,0.1)] hover:border-[#20B2AA] flex flex-col"
                >
                  <h2 className="text-xl font-bold text-[#0077B6] mb-6 flex items-center">
                    <FaCalendarAlt className="h-5 w-5 mr-2 text-[#20B2AA]" />
                    {format(selectedDate, 'MMMM d, yyyy')}
                  </h2>
                  {getAppointmentsForDay(selectedDate).length > 0 ? (
                    <div className="space-y-4 overflow-y-auto flex-1">
                      {getAppointmentsForDay(selectedDate).map((appt) => (
                        <motion.div
                          key={appt._id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="p-4 bg-[rgba(0,119,182,0.05)] border border-[rgba(0,119,182,0.1)] rounded-lg hover:bg-[rgba(0,119,182,0.1)] transition-colors"
                        >
                          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
                            <div className="flex items-center space-x-3">
                              <div className="bg-[rgba(0,119,182,0.1)] p-2 rounded-lg">
                                <FaStethoscope className="h-5 w-5 text-[#0077B6]" />
                              </div>
                              <div>
                                <h3 className="font-semibold text-gray-900">{appt.type}</h3>
                                <p className="text-sm text-gray-600 flex items-center">
                                  <FaUser className="h-3 w-3 mr-1" /> {appt.patient?.fullName || appt.fullName || appt.patientName || 'Unknown'}
                                </p>
                                <p className="text-sm text-gray-600 flex items-center">
                                  <FaNotesMedical className="h-3 w-3 mr-1" /> {appt.chiefComplaint}
                                </p>
                              </div>
                            </div>
                            <span className="mt-2 sm:mt-0 text-sm font-medium text-[#0077B6] bg-[rgba(0,119,182,0.1)] px-3 py-1 rounded-full flex items-center">
                              <FaClock className="h-3 w-3 mr-1" /> {appt.time}
                            </span>
                          </div>
                          {appt.notes && (
                            <div className="mt-2 text-sm text-gray-500 flex items-start">
                              <FaNotesMedical className="h-3 w-3 mt-1 mr-1 flex-shrink-0 text-[#20B2AA]" />
                              <span>{appt.notes}</span>
                            </div>
                          )}
                        </motion.div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex-1 flex items-center justify-center">
                      <div className="text-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-gray-400 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                        <h3 className="text-lg font-medium text-gray-900">No appointments</h3>
                        <p className="mt-1 text-gray-500">Schedule a new appointment to get started.</p>
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={handleAddAppointment}
                          className="mt-4 px-4 py-2 bg-gradient-to-r from-[#0077B6] to-[#20B2AA] text-white rounded-full text-sm font-medium transition-all duration-300 shadow-md hover:shadow-lg"
                        >
                          Add Appointment
                        </motion.button>
                      </div>
                    </div>
                  )}
                </motion.div>
              </motion.div>
            </motion.div>

            {/* Add Appointment Modal */}
            {showModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="bg-white rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto shadow-2xl border border-gray-100"
                >
                  <div className="p-6 border-b flex justify-between items-center bg-[rgba(0,119,182,0.05)]">
                    <h2 className="text-xl font-semibold text-[#0077B6]">New Appointment</h2>
                    <button
                      onClick={() => setShowModal(false)}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <FaTimes className="h-6 w-6" />
                    </button>
                  </div>
                  <form onSubmit={handleSubmitAppointment} className="p-6 space-y-6">
                    {error && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="mb-4 p-3 bg-red-50 border-l-4 border-red-500 rounded-lg shadow-sm"
                      >
                        {error}
                      </motion.div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Date*</label>
                      <div className="relative">
                        <FaCalendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                          type="date"
                          name="date"
                          value={newAppointment.date}
                          onChange={handleInputChange}
                          className="pl-10 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Time*</label>
                      <div className="relative">
                        <FaClock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                          type="time"
                          name="time"
                          value={newAppointment.time}
                          onChange={handleInputChange}
                          className="pl-10 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                          required
                          placeholder="Enter appointment time"
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Appointments are blocked for 30 minutes to prevent conflicts</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Type*</label>
                      <select
                        name="type"
                        value={newAppointment.type}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                        required
                      >
                        <option value="checkup">Checkup</option>
                        <option value="cleaning">Cleaning</option>
                        <option value="filling">Filling</option>
                        <option value="extraction">Extraction</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Chief Complaint (Optional)</label>
                      <textarea
                        name="chiefComplaint"
                        value={newAppointment.chiefComplaint}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                        rows="3"
                        placeholder="Describe the patient's main concern (optional)"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Notes (Optional)</label>
                      <textarea
                        name="notes"
                        value={newAppointment.notes}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                        rows="3"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Patient*</label>
                      <div className="flex space-x-2">
                        <button
                          type="button"
                          onClick={openPatientModal}
                          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-700 hover:bg-gray-50 focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA] flex items-center justify-center"
                        >
                          <FaUser className="h-4 w-4 mr-2" />
                          {selectedPatient ? 'Change Patient' : 'Select Patient'}
                        </button>
                      </div>
                      {selectedPatient ? (
                        <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                          <div className="text-sm text-green-800">
                            <strong>Selected:</strong> {selectedPatient.fullName || selectedPatient.name} (ID: {selectedPatient.nationalId || selectedPatient.id})
                          </div>
                        </div>
                      ) : (
                        <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <div className="text-sm text-blue-800">
                            <strong>Note:</strong> Click "Select Patient" to choose from your assigned patients. 
                            If you don't see any patients, contact your administrator to get patients assigned to you.
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="flex justify-end space-x-4">
                      <motion.button
                        type="button"
                        onClick={() => setShowModal(false)}
                        className="px-6 py-2 border border-[#20B2AA] text-[#20B2AA] rounded-full hover:bg-[rgba(32,178,170,0.1)] font-medium transition-all duration-300"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        Cancel
                      </motion.button>
                      <motion.button
                        type="submit"
                        className="px-6 py-2 bg-gradient-to-r from-[#0077B6] to-[#20B2AA] text-white rounded-full font-medium transition-all duration-300 shadow-md hover:shadow-lg"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}

                      >
                        Save
                      </motion.button>
                    </div>
                  </form>
                </motion.div>
              </div>
            )}

            {/* Patient Selection Modal */}
            {showPatientModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl border border-gray-100"
                >
                  <div className="p-6 border-b flex justify-between items-center bg-[rgba(0,119,182,0.05)]">
                    <h2 className="text-xl font-semibold text-[#0077B6]">Select Patient</h2>
                    <button
                      onClick={() => setShowPatientModal(false)}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <FaTimes className="h-6 w-6" />
                    </button>
                  </div>
                  
                  <div className="p-6">
                    {/* Search Bar */}
                    <div className="mb-6">
                      <div className="relative">
                        <FaUser className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                          type="text"
                          value={patientSearchModal}
                          onChange={handlePatientModalSearch}
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                          placeholder="Search patients by name or national ID..."
                        />
                      </div>
                    </div>

                    {/* Patients List */}
                    {loadingPatients ? (
                      <div className="flex justify-center items-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#20B2AA]"></div>
                      </div>
                    ) : (
                      <div className="space-y-3 max-h-96 overflow-y-auto">
                        {filteredPatients.length > 0 ? (
                          filteredPatients.map((patient) => (
                            <motion.div
                              key={patient.nationalId || patient.id || patient._id}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              onClick={() => selectPatientFromModal(patient)}
                              className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-all duration-200 hover:shadow-sm"
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <h3 className="font-semibold text-gray-900 text-lg">
                                    {patient.fullName || patient.name || 'Unknown Name'}
                                  </h3>
                                  <div className="flex items-center space-x-4 text-sm text-gray-600 mt-1">
                                    <span>ID: {patient.nationalId || patient.id || 'N/A'}</span>
                                    {patient.age && <span>Age: {patient.age}</span>}
                                    {patient.gender && <span>Gender: {patient.gender}</span>}
                                    {patient.phoneNumber && <span>Phone: {patient.phoneNumber}</span>}
                                  </div>
                                </div>
                                <div className="text-[#20B2AA]">
                                  <FaUser className="h-5 w-5" />
                                </div>
                              </div>
                            </motion.div>
                          ))
                        ) : (
                          <div className="text-center py-12">
                            <FaUser className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">
                              {patientSearchModal ? 'No patients found' : 'No patients available'}
                            </h3>
                            <p className="text-gray-500 mb-4">
                              {patientSearchModal 
                                ? 'Try adjusting your search terms' 
                                : 'You don\'t have any assigned patients yet. This usually means:'
                              }
                            </p>
                            {!patientSearchModal && (
                              <div className="text-sm text-gray-600 space-y-2">
                                <p>• Patients need to be assigned to you by an admin</p>
                                <p>• Or patients need to be created with your ID as drId</p>
                                <p>• Contact your administrator for assistance</p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Results Count */}
                    {filteredPatients.length > 0 && (
                      <div className="mt-4 text-sm text-gray-500 text-center">
                        Showing {filteredPatients.length} of {allPatients.length} patients
                      </div>
                    )}
                  </div>
                </motion.div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Calendar;
